import { Link } from "react-router-dom";
import { Pencil, Trash2 } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Pages } from "@/constants/enums";
import { appPath } from "@/constants/paths";
import type { DashboardRoleItem } from "@/features/roles/types/role";
import { useDeleteRole } from "@/features/roles/hooks/useRolesMutations";

interface RolesTableProps {
  roles: DashboardRoleItem[];
}

export function RolesTable({ roles }: RolesTableProps) {
  const deleteMutation = useDeleteRole();

  const handleDelete = async (role: DashboardRoleItem) => {
    if (role.isSystem) return;
    if (role.userCount > 0) return;
    if (!window.confirm(`هل أنت متأكد من حذف الدور "${role.name}"؟`)) return;
    await deleteMutation.mutateAsync(role.code);
  };

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead className="text-right">الاسم</TableHead>
          <TableHead className="text-right">النوع</TableHead>
          <TableHead className="text-right">المستخدمون</TableHead>
          <TableHead className="text-right">الوصف</TableHead>
          <TableHead className="text-right">إجراءات</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {roles.map((role) => (
          <TableRow key={role.code}>
            <TableCell className="font-medium">{role.name}</TableCell>
            <TableCell>
              <Badge variant={role.isSystem ? "secondary" : "outline"}>
                {role.isSystem ? "نظام" : "مخصص"}
              </Badge>
            </TableCell>
            <TableCell>{role.userCount}</TableCell>
            <TableCell className="max-w-xs truncate text-muted-foreground">
              {role.description || "—"}
            </TableCell>
            <TableCell>
              <div className="flex justify-end gap-2">
                {!role.isSystem && (
                  <>
                    <Button variant="ghost" size="icon" asChild>
                      <Link
                        to={appPath(Pages.ROLES, encodeURIComponent(role.code), "edit")}
                      >
                        <Pencil className="size-4" />
                      </Link>
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      disabled={role.userCount > 0 || deleteMutation.isPending}
                      onClick={() => handleDelete(role)}
                    >
                      <Trash2 className="size-4 text-destructive" />
                    </Button>
                  </>
                )}
              </div>
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}
